import { useState } from "react";
import {
  useDeleteRestaurantRequestMutation,
  useFetchRestaurantRequestsQuery,
} from "../store";
import Pagination from "../components/common/Pagination.tsx";
import { FaRegTrashAlt } from "react-icons/fa";
import TextField from "../components/common/TextField.tsx";
import useDebounce from "../hooks/useDebounce.tsx";
import {
  ApplicantRoleLabels,
  RestaurantRequest,
  WeeklyOrdersLabels,
} from "../types";
import { showToast, ToastType } from "../utils/toast.utils.ts";
import Modal from "../components/common/Modal.tsx";
import Button from "../components/common/Button.tsx";
import { centeredTdClasses, tdClasses, thClasses } from "../configs";

const RestaurantApplications: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [displayName, setName] = useState<string>("");
  const debouncedDisplayName = useDebounce(displayName, 400);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [requestToDelete, setRequestToDelete] =
    useState<RestaurantRequest | null>(null);

  const [deleteRestaurantRequest] = useDeleteRestaurantRequestMutation();

  const confirmDelete = (requestToDelete: RestaurantRequest) => {
    setRequestToDelete(requestToDelete);
    setShowConfirmModal(true);
  };

  const handleDeleteRequest = async () => {
    if (!requestToDelete) return;
    const result = await deleteRestaurantRequest(requestToDelete._id);
    if (!result.error) {
      showToast("Demande supprimée", ToastType.SUCCESS);
    }
    setShowConfirmModal(false);
    setRequestToDelete(null);
  };

  const { data: requests } = useFetchRestaurantRequestsQuery({
    page,
    perPage,
    displayName: debouncedDisplayName,
  });

  return (
    <div className="w-full flex flex-col mx-auto p-4">
      <div className="w-1/4 mb-4">
        <TextField
          id="displayName"
          value={displayName}
          onChange={(e) => setName((e.target as HTMLInputElement).value)}
          placeholder="Nom du restaurant"
        />
      </div>
      <table
        className="table-fixed w-full rounded-xl"
        style={{ borderRadius: "50px" }}
      >
        <colgroup>
          <col className="w-1/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
          <col className="w-2/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
          <col className="w-1/12" />
        </colgroup>
        <thead>
          <tr>
            <th
              key={0}
              className={thClasses}
            >
              Nom restaurant
            </th>
            <th
              key={0}
              className={thClasses}
            >
              Adresse
            </th>
            <th
              key={0}
              className={thClasses}
            >
              Ville
            </th>
            <th
              key={0}
              className={thClasses}
            >
              Commandes prévues
            </th>
            <th
              key={0}
              className={thClasses}
            >
              Role demandeur
            </th>
            <th
              key={1}
              className={thClasses}
            >
              Nom demandeur
            </th>
            <th
              key={1}
              className={thClasses}
            >
              Prénom demandeur
            </th>
            <th
              key={2}
              className={thClasses}
            >
              Email
            </th>
            <th
              key={3}
              className={thClasses}
            >
              Téléphone
            </th>
            <th
              key={6}
              className={thClasses}
            >
              Date de création
            </th>
            <th
              key={7}
              className={thClasses}
            >
              Supprimer
            </th>
          </tr>
        </thead>
        <tbody>
          {requests?.data?.map((row) => {
            return (
              <tr key={row._id}>
                <td className={tdClasses}> {row.displayName} </td>
                <td className={tdClasses}> {row.address} </td>
                <td className={centeredTdClasses}> {row.city} </td>
                <td className={centeredTdClasses}>
                  {WeeklyOrdersLabels[row.weeklyOrders]}
                </td>
                <td className={tdClasses}>{ApplicantRoleLabels[row.role]}</td>
                <td className={tdClasses}> {row.lastName} </td>
                <td className={tdClasses}> {row.firstName} </td>
                <td className={tdClasses}> {row.email} </td>
                <td className={tdClasses}> {row.phone} </td>
                <td className={tdClasses}> {row.createdAt} </td>
                <td className={tdClasses}>
                  <div className="w-full flex justify-center">
                    <FaRegTrashAlt
                      className="w-8 text-primary cursor-pointer"
                      onClick={() => {
                        confirmDelete(row);
                      }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        paginationInfo={{
          page,
          perPage,
          count: requests?.paginationInfo.count || 1,
        }}
        setPage={setPage}
        setPerPage={setPerPage}
      />
      {showConfirmModal && (
        <Modal
          onClose={() => setShowConfirmModal(false)}
          actionBar={
            <>
              <Button
                className="bg-gray-300 px-4 py-2 rounded mr-2"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="bg-primary text-white px-4 py-2 rounded"
                onClick={handleDeleteRequest}
              >
                Supprimer
              </Button>
            </>
          }
        >
          <div className="bg-white text-center font-semibold">
            Êtes-vous sûr de vouloir supprimer la demande de{" "}
            {`${requestToDelete?.firstName} ${requestToDelete?.lastName}`} ?
          </div>
        </Modal>
      )}
    </div>
  );
};

export default RestaurantApplications;
